'use client';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState, ChangeEvent } from 'react';
import cn from 'classnames';

import Search from 'public/img/icon/search-51x53.svg';

import CMSResponsiveImage from '@/components/common/CMSResponsiveImage/CMSResponsiveImage';
import Text from '@/components/common/Text/Text';
import SearchBarTextElement from '@/components/common/SearchBarTextElement/SearchBarTextElement';
import Select from '@/components/common/Select/Select';
import PerkModal from '@/components/modal/PerkModal/PerkModal';
import { useModal } from '@/components/hook/modal';
import LoginModal from '@/components/modal/LoginModal/LoginModal';

import { Perk, PerkCategory } from '@/types/Perk';
import { ALL_PERK_CATEGORIES } from '@/const/perk';
import { SIGNUP_ROUTES } from '@/const/signup';

import './PerksGrid.style.scss';

type PerksGridProps = {
  className?: string;
  perks: Perk[];
};

function PerksGrid(props: PerksGridProps) {
  const { className = '', perks } = props;

  const [searchValue, setSearchValue] = useState<string>('');
  const [categoryValue, setCategoryValue] = useState<
    PerkCategory | undefined
  >();

  const [sortByValue, setSortByValue] = useState<string | undefined>();

  const modalTools = useModal();
  const router = useRouter();

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget;
    setSearchValue(value);
  };

  const handleCategoryChange = (
    event: ChangeEvent<HTMLSelectElement>
  ): void => {
    const { value } = event.currentTarget;
    const category = ALL_PERK_CATEGORIES.find((c) => c.value === value);

    if (category) {
      setCategoryValue(category);
    }
  };

  const handleSortByChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { value } = event.currentTarget;
    setSortByValue(value);
  };

  const runSearch = (): void => {
    // Run a search here
  };

  const handleLoginLinkClick = (perk: Perk) => {
    modalTools.closeModal();

    modalTools.showModal(
      <LoginModal
        initialValues={{ username: '', password: '' }}
        onClose={modalTools.closeModal}
        onSubmitSuccess={() => {
          modalTools.closeModal();
          modalTools.showModal(
            <PerkModal
              onClose={modalTools.closeModal}
              data={perk}
              onLoginLinkClick={handleLoginLinkClick}
              onApplyLinkClick={handleApplyLinkClick}
            />,
            { topClassName: 'bg-gold' }
          );
        }}
      />
    );
  };

  const handleApplyLinkClick = () => {
    router.push(SIGNUP_ROUTES.ACCOUNT);
    modalTools.closeModal();
  };

  const handlePerkClick = (perk: Perk): void => {
    modalTools.showModal(
      <PerkModal
        onClose={modalTools.closeModal}
        data={perk}
        onLoginLinkClick={handleLoginLinkClick}
        onApplyLinkClick={handleApplyLinkClick}
      />,
      { topClassName: 'bg-gold' }
    );
  };

  return (
    <div className={cn('PerksGrid', className)}>
      <div className='PerksGrid-section-search'>
        <form
          className='PerksGrid-search-container'
          onSubmit={(event) => {
            event.preventDefault();
            runSearch();
          }}
        >
          <SearchBarTextElement
            className='PerksGrid-search-input'
            onChange={handleSearchChange}
            value={searchValue}
            placeholder='Search Perks'
          />
          <Image
            className='PerksGrid-search-icon cursor'
            src={Search}
            alt='Search perks'
            height={24}
            width={24}
            onClick={runSearch}
          />
        </form>
        <div className='PerksGrid-dropdowns-container'>
          <Select
            className='PerksGrid-dropdown PerksGrid-dropdown-category'
            variant='bolded'
            value={categoryValue?.value}
            onChange={handleCategoryChange}
          >
            <option>CATEGORY</option>
            {ALL_PERK_CATEGORIES.map((category) => {
              return (
                <option
                  key={category.value}
                  value={category.value}
                >
                  {category.displayText}
                </option>
              );
            })}
          </Select>
          <Select
            className='PerksGrid-dropdown PerksGrid-dropdown-category'
            variant='bolded'
            value={sortByValue}
            onChange={handleSortByChange}
          >
            <option>SORT BY</option>
          </Select>
        </div>
      </div>
      <div className='PerksGrid-grid'>
        {perks.map((perkData, index) => {
          const { logo } = perkData;
          return (
            <div
              className='PerksGrid-item-container cursor'
              onClick={(): void => {
                handlePerkClick(perkData);
              }}
              key={index}
            >
              <div className='PerksGrid-item'>
                <CMSResponsiveImage
                  className='PerksGrid-CMSResponsiveImage'
                  data={logo}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PerksGrid;
